<template>
  <v-card class="fill-height fluid">
    <v-toolbar dark color="primary darken-1">
      <v-toolbar-title>Defect Notice Log</v-toolbar-title>
    </v-toolbar>
    <v-card-text>
      <div v-if="$wait.is('loading audits')" class="text-center pa-4">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
        <div class="mt-2">Loading audit records...</div>
      </div>
      <v-expansion-panels v-else-if="audits && audits.length > 0" multiple>
        <v-expansion-panel v-for="audit in audits" :color="audit.color" :key="audit.id">
          <v-expansion-panel-header>
            <template v-slot:default="{ open }">
              <v-row no-gutters>
                <v-col cols="1">
                  <v-icon :color="audit.color">
                    {{ audit.icon }}
                  </v-icon>
                </v-col>
                <v-col cols="5">
                  <strong>{{ audit.title }}</strong
                  ><br />
                </v-col>
                <v-col cols="5">
                  <div align="right">
                    <small>
                      {{
                        moment(audit.date)
                          .local()
                          .format('D MMM YYYY hh:mma')
                      }}<br />
                      {{ audit.username }}
                    </small>
                  </div>
                </v-col>
              </v-row>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <ul v-for="change in audit.detail">
              <li v-if="change.oldValue && change.newValue">
                Changed <strong>{{ change.propertyName }}</strong> from <v-chip class="wrap-chip" small color="orange" outlined label>{{ change.oldValue }}</v-chip> to
                <v-chip small color="green" outlined label>{{ change.newValue }}</v-chip>
              </li>
              <li v-else-if="change.oldValue && !change.newValue">
                Removed <v-chip class="wrap-chip" small color="orange" outlined label>{{ change.oldValue }}</v-chip> from <strong>{{ change.propertyName }}</strong>
              </li>
              <li v-else-if="!change.oldValue && change.newValue">
                Set <strong>{{ change.propertyName }}</strong> to <v-chip class="wrap-chip" small color="green" outlined label>{{ change.newValue }}</v-chip>
              </li>
            </ul>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <div v-else>No audit records found.</div>
    </v-card-text>
  </v-card>
</template>

<script>
import Audits from '@/models/IDefectNotice.ts';
import moment from 'moment/moment';

export default {
  mixins: [Audits],
  props: {
    defectNoticeId: {
      type: String,
      required: true
    },
    workSessions: {
      hivisConfirmationTimestamp: Date,
      totalSecondsWorked: Number,
      user: String
    }
  },
  data() {
    return {
      audits: []
    };
  },
  methods: {
    async loadAudits() {
      try {
        this.$wait.start('loading audits');
        let response = await this.$api.get(`audits?types=GateKeeper.Domain.DefectNotice&entityId=${this.defectNoticeId}`);
        this.audits = response.data.data
          .map(audit => ({
            id: audit.id,
            username: audit.username,
            date: moment(audit.dateChanged),
            color: 'blue',
            icon: 'mdi-clipboard-edit',
            title: generateAuditTitle(audit),
            detail: audit.propertyChanges
          }))
          .sort((a, b) => {
            return new Date(b.date) - new Date(a.date);
          });
        function generateAuditTitle(audit) {
          if (audit.action.toLowerCase() === 'added') {
            return audit.entityName.split('.')[audit.entityName.split('.').length - 1] + ' Created';
          } else if (audit.action.toLowerCase() === 'modified') {
            return audit.entityName.split('.')[audit.entityName.split('.').length - 1] + ' Updated';
          } else {
            return audit.entityName.split('.')[audit.entityName.split('.').length - 1] + ' ' + audit.action.toLowerCase();
          }
        }
      } catch (error) {
        this.$toast.error('There was an error loading defect notice audit records');
        this.audits = [];
      } finally {
        this.$wait.end('loading audits');
      }
    }
  },
  watch: {
    defectNoticeId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadAudits();
        }
      }
    }
  },
  beforeDestroy() {
    // Clear audits when component is destroyed (dialog closes or panel is hidden)
    this.audits = [];
  }
};
</script>

<style scoped>
.wrap-chip {
  height: auto !important;
  min-height: 24px; /* keep small chip baseline */
  max-width: 100%;
  white-space: normal; /* allow wrapping if parent styles apply */
}
.wrap-chip .v-chip__content {
  white-space: normal; /* enable text wrapping inside chip */
  word-break: break-word; /* break long words/IDs */
  overflow-wrap: anywhere; /* modern fallback for long tokens */
}
</style>
